import { readSelectableStreamThemeId } from '@ffs-studio/protocol';
import type { StreamEffectStyle } from './stream-api';

export type StreamProfile = {
  id: string;
  name: string;
  style: StreamEffectStyle;
  welcomeEnabled: boolean;
  alertsEnabled: boolean;
  wamEnabled: boolean;
  commandSet: unknown[];
  audioSettings: Record<string, unknown>;
  moduleSettings: Record<string, unknown>;
  safeModeDefaults: Record<string, unknown>;
};

export type StreamConfiguration = {
  rehearsalEnabled: boolean;
  safeModeEnabled: boolean;
  activeProfileId: string | null;
  profiles: StreamProfile[];
};

export type StreamProfileDraft = Omit<StreamProfile, 'id'> & { id: string | null };

type Catalog = {
  modes?: Record<string, unknown> | null;
  profiles?: Array<Record<string, unknown>>;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(body.message ?? body.error ?? `Request failed with HTTP ${response.status}.`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json().catch(() => ({}))) as T;
}

function bool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function array(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function style(value: unknown): StreamEffectStyle {
  return readSelectableStreamThemeId(value) ?? 'black-yellow';
}

function mapProfile(row: Record<string, unknown>): StreamProfile | null {
  const id = typeof row.id === 'string' ? row.id : '';
  const name = typeof row.name === 'string' ? row.name.trim() : '';
  if (!id || !name) return null;
  return {
    id,
    name,
    style: style(row.style),
    welcomeEnabled: bool(row.welcome_enabled, true),
    alertsEnabled: bool(row.alerts_enabled, true),
    wamEnabled: bool(row.wam_enabled, true),
    commandSet: array(row.command_set),
    audioSettings: object(row.audio_settings),
    moduleSettings: object(row.module_settings),
    safeModeDefaults: object(row.safe_mode_defaults),
  };
}

export async function loadStreamConfiguration(): Promise<StreamConfiguration> {
  const catalog = await request<Catalog>('/api/v1/foundation/catalog');
  const modes = catalog.modes ?? {};
  const profiles = (catalog.profiles ?? []).map(mapProfile).filter((item): item is StreamProfile => item !== null);
  return {
    rehearsalEnabled: modes.rehearsal_enabled === true,
    safeModeEnabled: modes.safe_mode_enabled === true,
    activeProfileId: typeof modes.active_profile_id === 'string' ? modes.active_profile_id : null,
    profiles: profiles.sort((left, right) => left.name.localeCompare(right.name, 'en-GB', { numeric: true })),
  };
}

export function saveSafetyModes(rehearsalEnabled: boolean, safeModeEnabled: boolean): Promise<unknown> {
  return request('/api/v1/foundation/modes', {
    method: 'PUT',
    body: JSON.stringify({
      rehearsalEnabled,
      safeModeEnabled,
      suppressedFeatures: safeModeEnabled
        ? ['viewer-sounds', 'optional-modules', 'nonessential-alerts', 'raid-finder', 'print-jobs', 'rehearsal-actions']
        : [],
    }),
  });
}

export function runWelcomeRehearsal(
  kind: 'welcome-fallback' | 'welcome-personalised',
  login?: string,
): Promise<unknown> {
  return request('/api/v1/foundation/rehearsal/run', {
    method: 'POST',
    body: JSON.stringify({
      kind,
      ...(kind === 'welcome-personalised' ? { login: login?.trim() ?? '' } : {}),
    }),
  });
}

function profilePayload(draft: StreamProfileDraft) {
  return {
    name: draft.name.trim(),
    style: draft.style,
    welcomeEnabled: draft.welcomeEnabled,
    alertsEnabled: draft.alertsEnabled,
    wamEnabled: draft.wamEnabled,
    commandSet: draft.commandSet,
    audioSettings: draft.audioSettings,
    moduleSettings: draft.moduleSettings,
    safeModeDefaults: draft.safeModeDefaults,
  };
}

export function saveStreamProfile(draft: StreamProfileDraft): Promise<unknown> {
  const path = draft.id
    ? `/api/v1/foundation/profiles/${encodeURIComponent(draft.id)}`
    : '/api/v1/foundation/profiles';
  return request(path, {
    method: draft.id ? 'PUT' : 'POST',
    body: JSON.stringify(profilePayload(draft)),
  });
}

export function applyStreamProfile(profileId: string): Promise<unknown> {
  return request(`/api/v1/foundation/profiles/${encodeURIComponent(profileId)}/apply`, { method: 'POST' });
}

export function deleteStreamProfile(profileId: string): Promise<void> {
  return request(`/api/v1/foundation/profiles/${encodeURIComponent(profileId)}`, { method: 'DELETE' });
}
